/**
 * Puzzle Parser
 *
 * Parses puzzle definition files in 5–7 line format:
 * - Lines 1-4: Grid columns (6 clues per line, comma-separated)
 * - Line 5: Final answer with optional alternates (pipe-delimited)
 * - Line 6 (optional): Category descriptions (pipe-delimited, one per column)
 * - Line 7 (optional): Joiner words (pipe-delimited per column, comma per pair; empty = null)
 *
 * Clue syntax:
 * - Hidden: /prefix (e.g., "/bite")
 * - Visible: No prefix (e.g., "dust")
 *
 * Each line 1-4 represents one complete column (all 6 rows)
 */

import type { PuzzleData, Clue, Answer, PuzzleJSON } from '../types';
import { COLUMNS, ROWS } from './constants';

/**
 * Parse a puzzle definition from text content
 *
 * @param content - Raw text content from puzzle file
 * @param date - Puzzle date (YYYY-MM-DD)
 * @returns Parsed puzzle data
 * @throws Error if format is invalid
 */
export function parsePuzzle(content: string, date: string): PuzzleData {
  const lines = content
    .trim()
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);

  if (lines.length < 5) {
    throw new Error(`Invalid puzzle format: expected 5 lines, got ${lines.length}`);
  }

  // Parse grid (first 4 lines = 4 columns)
  const grid: Clue[][] = [];

  for (let colIndex = 0; colIndex < COLUMNS; colIndex++) {
    const cluesInColumn = lines[colIndex].split(',').map(clue => clue.trim());

    if (cluesInColumn.length !== ROWS) {
      throw new Error(
        `Invalid puzzle format: column ${colIndex} has ${cluesInColumn.length} clues, expected ${ROWS}`
      );
    }

    // Parse all 6 clues for this column
    grid[colIndex] = cluesInColumn.map(clueText => {
      const isHidden = clueText.startsWith('/');
      const displayText = isHidden ? clueText.slice(1) : clueText;

      return {
        text: displayText,
        isHidden: isHidden,
        isRevealed: !isHidden, // Visible clues are pre-revealed
      };
    });
  }

  // Parse final answer (line 5)
  const finalAnswer = parseAnswer(lines[4]);

  // Parse optional categories (line 6), joiners (line 7), column answers (line 8)
  let categories: string[] | undefined;
  let joiners: (string | null)[][] | undefined;
  let columnAnswers: Record<number, Answer> | undefined;

  if (lines.length >= 6 && lines[5].length > 0) {
    categories = lines[5].split('|').map(s => s.trim());
  }

  if (lines.length >= 7 && lines[6].length > 0) {
    joiners = lines[6].split('|').map(colJoiners =>
      colJoiners.split(',').map(j => {
        const trimmed = j.trim();
        return trimmed.length > 0 ? trimmed : null;
      })
    );
  }

  // Column answers: semicolon-separated columns, pipe-separated alternates within each
  if (lines.length >= 8 && lines[7].length > 0) {
    const colAnswerParts = lines[7].split(';');
    const parsed: Record<number, Answer> = {};
    for (let i = 0; i < colAnswerParts.length && i < COLUMNS; i++) {
      const trimmed = colAnswerParts[i].trim();
      if (trimmed.length > 0) {
        parsed[i] = parseAnswer(trimmed);
      }
    }
    if (Object.keys(parsed).length > 0) {
      columnAnswers = parsed;
    }
  }

  return {
    date,
    grid,
    finalAnswer,
    columnAnswers,
    categories,
    joiners,
  };
}

/**
 * Parse answer line with alternates
 *
 * Format: "primary|alternate1|alternate2"
 * Line breaks (<br>) are removed
 *
 * @param line - Answer line from puzzle file (final answer or column answer)
 * @returns Answer object with primary and alternates
 */
function parseAnswer(line: string): Answer {
  // Remove line breaks if present
  const cleanedLine = line.replace(/<br>/gi, '');

  // Split by pipe delimiter
  const parts = cleanedLine.split('|').map(s => s.trim()).filter(s => s.length > 0);

  if (parts.length === 0) {
    throw new Error('Invalid puzzle format: answer is empty');
  }

  return {
    primary: parts[0],
    alternates: parts.slice(1),
  };
}

// ─── PuzzleJSON ↔ PuzzleData Conversion ──────────────────

/**
 * Convert PuzzleJSON to the runtime PuzzleData format.
 */
export function parsePuzzleJSON(json: PuzzleJSON, date: string): PuzzleData {
  const grid: Clue[][] = [];
  const categories: string[] = [];
  const joiners: (string | null)[][] = [];
  const columnAnswers: Record<number, Answer> = {};

  for (let col = 0; col < COLUMNS; col++) {
    const colDef = json.columns[col];
    if (!colDef) {
      // Fallback: empty column
      grid[col] = Array.from({ length: ROWS }, () => ({
        text: '', isHidden: false, isRevealed: true,
      }));
      categories[col] = '';
      joiners[col] = [null, null, null];
      continue;
    }

    categories[col] = colDef.category;

    const colClues: Clue[] = [];
    const colJoiners: (string | null)[] = [];

    for (let p = 0; p < 3; p++) {
      const pair = colDef.pairs[p];
      if (!pair) {
        colClues.push({ text: '', isHidden: false, isRevealed: true });
        colClues.push({ text: '', isHidden: false, isRevealed: true });
        colJoiners.push(null);
        continue;
      }

      for (let slot = 0; slot < 2; slot++) {
        const isHidden = pair.hiddenIndex === slot;
        colClues.push({
          text: pair.clues[slot],
          isHidden,
          isRevealed: !isHidden,
        });
      }
      colJoiners.push(pair.joiner);
    }

    grid[col] = colClues;
    joiners[col] = colJoiners;

    // Column answer = pair 2's hidden clue
    const pair2 = colDef.pairs[2];
    if (pair2) {
      const answerText = pair2.clues[pair2.hiddenIndex];
      columnAnswers[col] = {
        primary: answerText,
        alternates: colDef.answerAlternates,
      };
    }
  }

  return {
    date,
    grid,
    finalAnswer: {
      primary: json.solution.primary,
      alternates: json.solution.alternates,
    },
    columnAnswers: Object.keys(columnAnswers).length > 0 ? columnAnswers : undefined,
    categories,
    joiners,
    displayHints: json.displayHints,
  };
}

/**
 * Convert PuzzleData back to PuzzleJSON format.
 */
export function puzzleDataToJSON(puzzle: PuzzleData): PuzzleJSON {
  const columns: PuzzleJSON['columns'] = [];

  for (let col = 0; col < COLUMNS; col++) {
    const pairs: PuzzleJSON['columns'][0]['pairs'] = [];

    for (let p = 0; p < 3; p++) {
      const topRow = p * 2;
      const top = puzzle.grid[col][topRow];
      const bottom = puzzle.grid[col][topRow + 1];
      pairs.push({
        clues: [top.text, bottom.text],
        hiddenIndex: top.isHidden ? 0 : 1,
        joiner: puzzle.joiners?.[col]?.[p] ?? null,
      });
    }

    const colAnswer = puzzle.columnAnswers?.[col];
    columns.push({
      category: puzzle.categories?.[col] ?? '',
      pairs,
      answerAlternates: colAnswer?.alternates ?? [],
    });
  }

  return {
    solution: {
      primary: puzzle.finalAnswer.primary,
      alternates: puzzle.finalAnswer.alternates,
    },
    columns,
    displayHints: puzzle.displayHints,
  };
}

/**
 * Import minimal text format into PuzzleJSON.
 *
 * Format: 5 lines
 *   Lines 1-4: Clue columns (6 clues comma-separated, /prefix = hidden)
 *   Line 5: Final solution (pipe-separated alternates)
 *
 * Categories, joiners, and column answer alternates are left blank.
 */
export function importTextToPuzzleJSON(text: string): PuzzleJSON {
  const puzzle = parsePuzzle(text, '');
  return puzzleDataToJSON(puzzle);
}

/**
 * Convert PuzzleJSON back to the text format for backward compatibility.
 * Produces 5-8 lines matching the original format.
 */
export function puzzleJSONToText(json: PuzzleJSON): string {
  const lines: string[] = [];

  // Lines 1-4: Grid columns
  for (let col = 0; col < COLUMNS; col++) {
    const colDef = json.columns[col];
    const clues: string[] = [];
    for (let p = 0; p < 3; p++) {
      const pair = colDef?.pairs[p];
      if (!pair) {
        clues.push('', '');
        continue;
      }
      for (let slot = 0; slot < 2; slot++) {
        const text = pair.clues[slot];
        clues.push(pair.hiddenIndex === slot ? `/${text}` : text);
      }
    }
    lines.push(clues.join(','));
  }

  // Line 5: Final answer with alternates
  const answerParts = [json.solution.primary, ...json.solution.alternates];
  lines.push(answerParts.join('|'));

  // Line 6: Categories
  const cats = json.columns.map(c => c?.category ?? '');
  if (cats.some(c => c.length > 0)) {
    lines.push(cats.join('|'));
  }

  // Line 8: Column answers (computed first to decide if joiners line is needed)
  const colAnswers = json.columns.map(c => {
    const pair2 = c?.pairs[2];
    if (!pair2) return '';
    const primary = pair2.clues[pair2.hiddenIndex];
    const alts = c.answerAlternates;
    return alts.length > 0 ? `${primary}|${alts.join('|')}` : primary;
  });
  const hasColAnswers = colAnswers.some(a => a.length > 0);

  // Line 7: Joiners — must always be present if column answers follow,
  // otherwise the column answers line shifts up and gets parsed as joiners.
  const joinerStrs = json.columns.map(c =>
    (c?.pairs ?? []).map(p => p.joiner ?? '').join(',')
  );
  const hasJoiners = joinerStrs.some(j => j.replace(/,/g, '').length > 0);
  if (hasJoiners || hasColAnswers) {
    lines.push(joinerStrs.join('|'));
  }

  if (hasColAnswers) {
    lines.push(colAnswers.join(';'));
  }

  return lines.join('\n');
}

/**
 * Load and parse puzzle, checking Supabase for a current puzzle first.
 *
 * Priority:
 * 1. Supabase puzzles_admin table (is_current = true)
 * 2. Static file at /puzzles/{date}.txt
 * 3. Fallback to /PuzzleDef-KFC.txt
 *
 * @param date - Puzzle date (YYYY-MM-DD), used as fallback
 * @returns Parsed puzzle data
 */
export async function loadPuzzle(date: string): Promise<PuzzleData> {
  // Try Supabase first
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (supabaseUrl && supabaseKey) {
      const response = await fetch(
        `${supabaseUrl}/rest/v1/puzzles_admin?is_current=eq.true&select=id,puzzle_number,puzzle_text,puzzle_json,date&limit=1`,
        {
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
          },
        }
      );

      if (response.ok) {
        const rows = await response.json();
        if (rows.length > 0) {
          const puzzleDate = rows[0].date || date;
          const puzzleId = rows[0].id as string | undefined;
          const puzzleNum = rows[0].puzzle_number as number | undefined;
          if (rows[0].puzzle_json) {
            const pd = parsePuzzleJSON(rows[0].puzzle_json, puzzleDate);
            pd.id = puzzleId;
            pd.puzzleNumber = puzzleNum;
            return pd;
          }
          const pd = parsePuzzle(rows[0].puzzle_text, puzzleDate);
          pd.id = puzzleId;
          pd.puzzleNumber = puzzleNum;
          return pd;
        }
      }
    }
  } catch (err) {
    console.warn('Could not load puzzle from Supabase, falling back to file', err);
  }

  // Fallback: static file
  try {
    const response = await fetch(`/puzzles/${date}.txt`);

    if (!response.ok) {
      throw new Error(`Failed to load puzzle: ${response.statusText}`);
    }

    const content = await response.text();
    return parsePuzzle(content, date);
  } catch (error) {
    console.warn(`Could not load puzzle for ${date}, using fallback`, error);

    const response = await fetch('/PuzzleDef-KFC.txt');
    const content = await response.text();
    return parsePuzzle(content, date);
  }
}
